// src/app/file-drop-upload/file-drop-upload.component.ts

import {
  Component,
  EventEmitter,
  Input,
  Output,
  HostListener,
  signal,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';

import { FormGroup } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { MenuModule } from 'primeng/menu';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';

import { ApiResponse } from '../../_service/data.service';
import { Attachment, FileUploadService } from '../../_service/file-upload.service';

interface FileEntry {
  _id: string;
  file: File;
  name: string;
  size: number;
  addedAt: Date;
  status: string;
  isValid: boolean;
  errorMessage?: string;
}

@Component({
  standalone: true,
  selector: 'app-file-upload',
  imports: [FormsModule, CommonModule, ButtonModule, MenuModule, DialogModule],
  templateUrl: './file-upload.component.html',
  // styleUrl: './file-upload.component.css',
})
export class FileUploadComponent implements OnInit, OnChanges {
  @Input() noteForm!: FormGroup;
  @Input() noteId: string = ''; // the note to which files are attached
  @Input() noteAttachments: any[] | null | undefined = [];
  @Input() readOnly: boolean = false;
  // @Output() filesChanged = new EventEmitter<FileEntry[]>();
  @Output() filesValid = new EventEmitter<File[]>();

  fileEntries: FileEntry[] = []; // attachments loaded from server PLUS changes made in this edit session
  attachments: Attachment[] = []; // attachments loaded from server

  fileInfos?: Observable<any>; // confirm if this is really needed (May 2025)

  fileMenuItems: MenuItem[] = [];
  selectedFileEntry: FileEntry | null = null;
  showRenameDialog = false;
  newFileName: string = '';
  fileToRename: FileEntry | null = null;
  renameErrorMsg: string = '';
  private lastLoadedNoteId: string = '';

  dragOver = signal(false);
  errorMsg: string | null = null;

  readonly maxSize = 10 * 1024 * 1024; // 10MB
  readonly allowedTypes = [
    'text/plain',
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  ];

  constructor(private messageService: MessageService, private uploadService: FileUploadService) {}

  ngOnInit(): void {
    console.log('***In file-upload-component: ngOnInit()');
    console.log('   noteID : ', this.noteId);
    console.log('   read-only status : ', this.readOnly);
    this.initializeMenus();
    this.loadAttachmentsIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readOnly'] && !changes['readOnly'].firstChange) {
      this.initializeMenus();
    }

    if (changes['noteId']) {
      this.loadAttachmentsIfNeeded();
    }
  }

  private loadAttachmentsIfNeeded(): void {
    const incomingNoteId = (this.noteId ?? '').trim();

    // Guard against async template binding phases where note id is briefly unavailable.
    if (!incomingNoteId) {
      this.attachments = [];
      this.fileEntries = [];
      this.lastLoadedNoteId = '';
      return;
    }

    if (this.lastLoadedNoteId === incomingNoteId) {
      return;
    }

    this.lastLoadedNoteId = incomingNoteId;
    this.loadAttachments();
  }

  convertAttachmentsToFileEntries(attachments: Attachment[]): FileEntry[] {
    return attachments.map((att: any) => {
      const fileName = String(att?.filename ?? att?.fileName ?? att?.FileName ?? att?.name ?? att?.Name ?? att?._id);
      const createdOn = att?.created_on ?? att?.createdOn ?? att?.created ?? att?.uploaded_on ?? att?.UploadDatum;
      const size = Number(att?.size ?? att?.Size ?? 0);

      return {
        _id: String(att?._id ?? att?.id ?? att?.attachmentId ?? fileName),
        file: new File([], fileName), // Placeholder file object with empty content
        name: fileName,
        size: Number.isFinite(size) ? size : 0,
        addedAt: createdOn ? new Date(createdOn) : new Date(),
        status: 'stored',
        isValid: true,
      };
    });
  }

  private mapNoteAttachmentFallbackToFileEntries(rawAttachments: any[] | null | undefined): FileEntry[] {
    if (!Array.isArray(rawAttachments) || rawAttachments.length === 0) {
      return [];
    }

    return rawAttachments
      .map((att: any) => {
        if (!att) {
          return null;
        }

        if (typeof att === 'string') {
          return {
            _id: att,
            file: new File([], att),
            name: att,
            size: 0,
            addedAt: new Date(),
            status: 'stored',
            isValid: true,
          } as FileEntry;
        }

        const id = att._id || att.id || att.attachmentId || att.filename || att.fileName || att.FileName || att.name || att.Name;
        const name = att.filename || att.fileName || att.FileName || att.name || att.Name || id || 'Attachment';
        const size = Number(att.size ?? att.Size ?? 0);
        const createdOn = att.created_on || att.createdOn || att.created || att.modified_on || att.uploaded_on || att.UploadDatum;

        if (!id) {
          return null;
        }

        return {
          _id: String(id),
          file: new File([], String(name)),
          name: String(name),
          size: Number.isFinite(size) ? size : 0,
          addedAt: createdOn ? new Date(createdOn) : new Date(),
          status: 'stored',
          isValid: true,
        } as FileEntry;
      })
      .filter((entry): entry is FileEntry => !!entry);
  }

  private enrichFallbackFileNames(entries: FileEntry[]): void {
    const unresolvedEntries = entries.filter(
      (entry) => !!entry._id && (!entry.name || entry.name === entry._id),
    );

    for (const entry of unresolvedEntries) {
      this.uploadService.getAttachmentInfo(entry._id).subscribe((meta) => {
        const resolvedName =
          (meta as any)?.filename ||
          (meta as any)?.fileName ||
          (meta as any)?.FileName ||
          (meta as any)?.name ||
          (meta as any)?.Name;
        if (resolvedName) {
          entry.name = String(resolvedName);
        }
      });
    }
  }

  loadAttachments(): void {
    if (!this.noteId?.trim()) {
      this.attachments = [];
      this.fileEntries = [];
      return;
    }

    this.uploadService.getFiles(this.noteId).subscribe({
      next: (data: Attachment[]) => {
        console.log('Attachments loaded:', data);
        if (Array.isArray(data) && data.length > 0) {
          this.attachments = data;
          this.fileEntries = this.convertAttachmentsToFileEntries(this.attachments);
        } else {
          this.attachments = [];
          this.fileEntries = this.mapNoteAttachmentFallbackToFileEntries(this.noteAttachments);
          this.enrichFallbackFileNames(this.fileEntries);
        }
      },
      error: (err) => {
        console.error('Error fetching attachments:', err);
        this.attachments = [];
        this.fileEntries = this.mapNoteAttachmentFallbackToFileEntries(this.noteAttachments);
        this.enrichFallbackFileNames(this.fileEntries);
      },
    });
  }

  @HostListener('dragover', ['$event'])
  onDragOver(evt: DragEvent) {
    if (!this.readOnly) {
      evt.preventDefault();
      this.dragOver.set(true);
    }
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(evt: DragEvent) {
    evt.preventDefault();
    this.dragOver.set(false);
  }

  @HostListener('drop', ['$event'])
  onDrop(evt: DragEvent) {
    if (this.readOnly) return; // exit in read-only mode!
    evt.preventDefault();
    this.dragOver.set(false);
    this.errorMsg = null;

    const files = Array.from(evt.dataTransfer?.files ?? []);
    const entries: FileEntry[] = [];

    for (const file of files) {
      const basicValid = this.allowedTypes.includes(file.type) && file.size <= this.maxSize;

      let isValid = basicValid;
      let errorMessage: string | undefined;

      if (!basicValid) {
        errorMessage = !this.allowedTypes.includes(file.type)
          ? `Invalid file type: ${file.name}`
          : `File too large: ${file.name}`;
      } else {
        const nameValidation = this.validateFileName(file.name);
        if (!nameValidation.valid) {
          isValid = false;
          errorMessage = nameValidation.error;
        }
      }

      const entry: FileEntry = {
        _id: '',
        file: file,
        name: file.name,
        size: file.size,
        addedAt: new Date(),
        status: isValid ? 'new' : 'invalid',
        isValid,
        errorMessage,
      };

      entries.push(entry);
    }

    this.fileEntries.push(...entries);

    const validFiles = entries.filter((e) => e.isValid).map((e) => e.file);
    if (validFiles.length > 0) {
      this.filesValid.emit(validFiles);
    }
  }

  onFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const fakeDropEvent = {
      dataTransfer: { files: input.files },
      preventDefault: () => {},
    } as DragEvent;

    this.onDrop(fakeDropEvent);
  }

  removeFile(entry: FileEntry) {
    if (entry.status === 'saved' || entry.status === 'renamed') entry.status = 'deleted';
    else {
      this.fileEntries = this.fileEntries.filter((e) => e !== entry);
    }
    // this.filesChanged.emit(this.getValidFiles());
  }

  initializeMenus(): void {
    const items: MenuItem[] = [
      {
        label: 'Download',
        icon: 'bi bi-download',
        styleClass: 'text-red-500',
        command: () => this.onSelectFileMenuItem('Download', this.selectedFileEntry),
      },
    ];

    if (!this.readOnly) {
      items.push(
        {
          label: 'Rename',
          icon: 'bi bi-pencil',
          styleClass: 'text-red-500',
          command: () => this.onSelectFileMenuItem('Rename', this.selectedFileEntry),
        },
        {
          label: 'Delete',
          icon: 'bi bi-trash-fill',
          styleClass: 'text-red-500',
          command: () => this.onSelectFileMenuItem('Delete', this.selectedFileEntry),
        },
      );
    }

    this.fileMenuItems = [{ items }];
  }

  showFileMenu(event: Event, menu: Menu, file: FileEntry): void {
    this.selectedFileEntry = file;
    menu.toggle(event);
  }

  onSelectFileMenuItem(action: string, file: FileEntry | null) {
    if (!file) return;

    switch (action) {
      case 'Download':
        console.log('Download:', file.name);
        this.onDownloadFile();
        break;
      case 'Rename':
        // console.log("Rename:", file.name);
        this.fileToRename = file;
        this.newFileName = file.name;
        this.showRenameDialog = true;
        break;
      case 'Delete':
        console.log('Delete:', file.name);
        this.removeFile(file);
        break;
    }
  }

  private validateFileName(name: string, excludeEntry?: FileEntry): { valid: boolean; error?: string } {
    const trimmed = name.trim();

    if (!trimmed) return { valid: false, error: 'Der Dateiname darf nicht leer sein.' };
    if (trimmed.length > 100) return { valid: false, error: 'Der Dateiname ist zu lang.' };
    if (/[\/\\<>:"|?*\x00-\x1F]/.test(trimmed))
      return {
        valid: false,
        error: 'Der Dateiname enthält ungültige Zeichen.',
      };
    if (trimmed.includes('..') || trimmed.includes('/'))
      return { valid: false, error: 'Ungültiger Pfad im Dateinamen.' };

    const duplicate = this.fileEntries.some(
      (entry) => entry !== excludeEntry && entry.name.toLowerCase() === trimmed.toLowerCase(),
    );

    if (duplicate)
      return {
        valid: false,
        error: 'Eine Datei mit diesem Namen existiert bereits.',
      };

    return { valid: true };
  }

  closeRename(): void {
    this.showRenameDialog = false;
    this.fileToRename = null;
    this.newFileName = '';
    this.renameErrorMsg = '';
  }

  confirmRename(): void {
    const trimmedName = this.newFileName.trim();

    if (!this.fileToRename) {
      this.closeRename();
      return;
    }

    const result = this.validateFileName(trimmedName, this.fileToRename);

    if (!result.valid) {
      this.renameErrorMsg = result.error!;
      return;
    }

    this.fileToRename.name = trimmedName;
    if (this.fileToRename.status === 'saved') this.fileToRename.status = 'renamed';

    this.closeRename();
  }

  formatFileSize(bytes?: number | null): string {
    if (!bytes || isNaN(bytes)) return '';

    if (!bytes) bytes = 0;
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let size = bytes / 1024;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    const rounded = size < 10 ? size.toFixed(1) : Math.round(size).toString();
    return `${rounded} ${units[unitIndex]}`;
  }

saveChanges(): Observable<ApiResponse<{
  AttachmentsAdded: string[];
  AttachmentsRenamed: string[];
  AttachmentsDeleted: string[];
}>> {
  console.debug('## In file-upload-component.saveChanges()');

  const newFiles = this.fileEntries.filter((e) => e.status === 'new' && e.isValid);
  const renamedFiles = this.fileEntries.filter((e) => e.status === 'renamed' && e.isValid);
  const deletedFiles = this.fileEntries.filter((e) => e.status === 'deleted');

  const uploadTasks: Observable<ApiResponse<Attachment>>[] = [];
  const renameTasks: Observable<ApiResponse<Attachment>>[] = [];
  const deleteTasks: Observable<ApiResponse<null>>[] = [];

  // 🧩 Arrays to collect affected IDs
  const addedIds: string[] = [];
  const renamedIds: string[] = [];
  const deletedIds: string[] = [];

  const makeError = <T>(message: string, code = 500): ApiResponse<T> => ({
    status: 'error',
    error: { code, message },
  });

  // ✅ Upload new files
  for (const entry of newFiles) {
    uploadTasks.push(
      this.uploadService.uploadAttachment(entry.file, this.noteId).pipe(
        map((response) => {
          if (response.status === 'success' && response.data?._id) {
            entry.status = 'saved';
            addedIds.push(response.data._id);

            // ✅ Update attachment references safely
            let currentAttachments: string[] = [];
            if (this.noteForm?.get('Attachments')) {
              currentAttachments = this.noteForm.get('Attachments')?.value ?? [];
            } else if ((this as any).note?.Attachments) {
              currentAttachments = (this as any).note.Attachments ?? [];
            }

            if (!currentAttachments.includes(response.data._id)) {
              currentAttachments.push(response.data._id);
              if (this.noteForm?.get('Attachments')) {
                this.noteForm.get('Attachments')?.setValue(currentAttachments);
              } else if ((this as any).note) {
                (this as any).note.Attachments = [...currentAttachments];
              }
            }

            console.log(`✅ Attachment ${response.data.filename} linked to note ${this.noteId}`);
          } else {
            console.warn(`⚠ Upload non-success for ${entry.name}:`, response.error?.message ?? 'unknown reason');
          }
          return response;
        }),
        catchError((err) => {
          const msg = err?.error?.error?.message || err?.message || `Error during upload: ${entry.name}`;
          console.error(`❌ Upload error for ${entry.name}:`, msg);
          return of(makeError<Attachment>(msg));
        }),
      ),
    );
  }

  // ✅ Rename existing files
  for (const entry of renamedFiles) {
    renameTasks.push(
      this.uploadService.renameAttachment(entry._id, entry.name, this.noteId).pipe(
        map((response) => {
          if (response.status === 'success') {
            entry.status = 'saved';
            renamedIds.push(entry._id);
            console.log(`✏️ Renamed attachment ${entry._id} to ${entry.name}`);
          } else {
            console.warn(`⚠ Rename failed for ${entry.name}:`, response.error?.message ?? 'unknown reason');
          }
          return response;
        }),
        catchError((err) => {
          const msg = err?.error?.error?.message || err?.message || `Error during rename: ${entry.name}`;
          console.error(`❌ Rename error for ${entry.name}:`, msg);
          return of(makeError<Attachment>(msg));
        }),
      ),
    );
  }

  // ✅ Delete files
  for (const entry of deletedFiles) {
    deleteTasks.push(
      this.uploadService.deleteAttachment(this.noteId, entry._id).pipe(
        map((response) => {
          if (response.status === 'success') {
            deletedIds.push(entry._id);
            this.fileEntries = this.fileEntries.filter((e) => e !== entry);

            // Remove from note’s attachments
            const currentAttachments: string[] = this.noteForm.get('Attachments')?.value ?? [];
            const updated = currentAttachments.filter((id) => id !== entry._id);
            this.noteForm.get('Attachments')?.setValue(updated);

            console.log(`🗑 Deleted attachment ${entry._id}`);
          } else {
            console.warn(`⚠ Delete failed for ${entry.name}:`, response.error?.message ?? 'unknown reason');
          }
          return response;
        }),
        catchError((err) => {
          const msg = err?.error?.error?.message || err?.message || `Error during delete: ${entry.name}`;
          console.error(`❌ Delete error for ${entry.name}:`, msg);
          return of(makeError<null>(msg));
        }),
      ),
    );
  }

  const allTasks: Observable<ApiResponse<unknown>>[] = [...uploadTasks, ...renameTasks, ...deleteTasks];

  if (allTasks.length === 0) {
    console.debug('No file changes detected.');
    return of({
      status: 'success',
      data: {
        AttachmentsAdded: [],
        AttachmentsRenamed: [],
        AttachmentsDeleted: [],
      },
    });
  }

  return forkJoin(allTasks).pipe(
    map((results: ApiResponse<unknown>[]) => {
      const failed = results.filter((r) => r.status !== 'success');
      if (failed.length > 0) {
        const combined = failed.map((f) => f.error?.message ?? 'Unknown error').join('; ');
        console.warn('⚠ Some attachment operations failed:', combined);
        return { status: 'error', error: { code: 500, message: combined } };
      }

      console.log('✅ All attachment operations completed successfully.');
      return {
        status: 'success',
        data: {
          AttachmentsAdded: addedIds,
          AttachmentsRenamed: renamedIds,
          AttachmentsDeleted: deletedIds,
        },
      };
    }),
  );
}

  OLD___saveChanges(): Observable<ApiResponse<null>> {
    console.debug('## In file-upload-component.saveChanges()');

    const newFiles = this.fileEntries.filter((e) => e.status === 'new' && e.isValid);
    const renamedFiles = this.fileEntries.filter((e) => e.status === 'renamed' && e.isValid);
    const deletedFiles = this.fileEntries.filter((e) => e.status === 'deleted');

    const uploadTasks: Observable<ApiResponse<Attachment>>[] = [];
    const renameTasks: Observable<ApiResponse<Attachment>>[] = [];
    const deleteTasks: Observable<ApiResponse<null>>[] = [];

    const makeError = <T>(message: string, code = 500): ApiResponse<T> => ({
      status: 'error',
      error: { code, message },
    });

    // Upload new files
    for (const entry of newFiles) {
      uploadTasks.push(
        this.uploadService.uploadAttachment(entry.file, this.noteId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during upload: ${entry.name}`))),
        ),
      );
    }

    // Rename existing files
    for (const entry of renamedFiles) {
      renameTasks.push(
        this.uploadService.renameAttachment(entry._id, entry.name, this.noteId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during rename: ${entry.name}`))),
        ),
      );
    }

    // Delete files
    for (const entry of deletedFiles) {
      deleteTasks.push(
        this.uploadService.deleteAttachment(this.noteId, entry._id).pipe(
          map((response) => {
            if (response.status === 'success') {
              this.fileEntries = this.fileEntries.filter((e) => e !== entry);
            }
            return response;
          }),
          catchError(() => of(makeError<null>(`Error during delete: ${entry.name}`))),
        ),
      );
    }

    const allTasks: Observable<ApiResponse<unknown>>[] = [...uploadTasks, ...renameTasks, ...deleteTasks];

    if (allTasks.length === 0) {
      return of({ status: 'success', data: null });
    }

    return forkJoin(allTasks).pipe(
      map((results: ApiResponse<unknown>[]) => {
        const failed = results.filter((r) => r.status !== 'success');
        if (failed.length > 0) {
          const combined = failed.map((f) => f.error?.message ?? 'Unknown error').join('; ');
          return { status: 'error', error: { code: 500, message: combined } };
        }
        return { status: 'success', data: null };
      }),
    );
  }

  saveChangesTo(saveToNoteId: string): Observable<ApiResponse<null>> {
    console.debug('## In file-upload-component.saveChangesTo()');

    const newFiles = this.fileEntries.filter((e) => e.status === 'new' && e.isValid);
    const savedFiles = this.fileEntries.filter((e) => e.status === 'saved' && e.isValid);
    const renamedFiles = this.fileEntries.filter((e) => e.status === 'renamed' && e.isValid);
    const deletedFiles = this.fileEntries.filter((e) => e.status === 'deleted');

    const uploadTasks: Observable<ApiResponse<Attachment>>[] = [];
    const copyTasks: Observable<ApiResponse<Attachment>>[] = [];
    const renameTasks: Observable<ApiResponse<Attachment>>[] = [];
    const deleteTasks: Observable<ApiResponse<null>>[] = [];

    const makeError = <T>(message: string, code = 500): ApiResponse<T> => ({
      status: 'error',
      error: { code, message },
    });

    // Upload new files to the target note
    for (const entry of newFiles) {
      uploadTasks.push(
        this.uploadService.uploadAttachment(entry.file, saveToNoteId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during upload: ${entry.name}`))),
        ),
      );
    }

    // Copy existing saved files from current note to target note
    for (const entry of savedFiles) {
      copyTasks.push(
        this.uploadService.copyAttachment(entry._id, this.noteId, saveToNoteId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during copy: ${entry.name}`))),
        ),
      );
    }

    // Rename files — only in new item (not original)
    for (const entry of renamedFiles) {
      renameTasks.push(
        this.uploadService.renameAttachment(entry._id, entry.name, saveToNoteId).pipe(
          map((response) => {
            if (response.status === 'success') entry.status = 'saved';
            return response;
          }),
          catchError(() => of(makeError<Attachment>(`Error during rename: ${entry.name}`))),
        ),
      );
    }

    // Delete files (if needed for cloned item cleanup)
    for (const entry of deletedFiles) {
      deleteTasks.push(
        this.uploadService.deleteAttachment(saveToNoteId, entry._id).pipe(
          map((response) => {
            if (response.status === 'success') {
              this.fileEntries = this.fileEntries.filter((e) => e !== entry);
            }
            return response;
          }),
          catchError(() => of(makeError<null>(`Error during delete: ${entry.name}`))),
        ),
      );
    }

    const allTasks: Observable<ApiResponse<unknown>>[] = [...uploadTasks, ...copyTasks, ...renameTasks, ...deleteTasks];

    if (allTasks.length === 0) {
      return of({ status: 'success', data: null });
    }

    return forkJoin(allTasks).pipe(
      map((results: ApiResponse<unknown>[]) => {
        const failed = results.filter((r) => r.status !== 'success');
        if (failed.length > 0) {
          const combined = failed.map((f) => f.error?.message ?? 'Unknown error').join('; ');
          return { status: 'error', error: { code: 500, message: combined } };
        }
        return { status: 'success', data: null };
      }),
    );
  }

  onDownloadFile(): void {
    const file = this.selectedFileEntry;
    if (!file || !file._id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No file selected',
        detail: 'Please select a file to download.',
      });
      return;
    }
    console.log('Downloading file:', file.name);
    this.uploadService.getFileForNote(this.noteId, file._id, file.name).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error during download',
          detail: `File could not be downloaded: ${err.message || err}`,
        });
      },
    });
  }

  downloadFile(entry: FileEntry): void {
    this.selectedFileEntry = entry;
    this.onDownloadFile();
  }

  //////////////////////////////////////////////////////////////////
  // Obsolete Functions!!
  //////////////////////////////////////////////////////////////////
}
